import { getConfig } from '@superbet-group/web.lib.config';
import { Country } from '@superbet-group/web.types';

import {
    MarketAllowance,
    OutcomeAllowance,
    SelectedOutcome,
    Config,
} from './types';
import { mapMarketPairsToMarketAllowances } from './mappers/mapMarketAllowances';
import { mapOutcomePairsToOutcomeAllowances } from './mappers/mapOutcomeAllowances';
import { validateSelectionCountLimit } from './validators/selectionCountValidator';
import { isMarketsCombinationValid } from './validators/marketValidator';
import { isOutcomesCombinationValid } from './validators/outcomeValidator';
import { Selection } from '@superbet-group/betting.lib.validation';
import { instanceGuard } from '@superbet-group/web.lib.utilities';
import SingletonError from '@core/services/common/errors/SingletonError';

export class Allowances {
    private static instance?: Allowances;
    private allowedMarketCombinations: Record<string, Set<MarketAllowance>> | undefined = undefined;
    private allowedOutcomeCombinations: Record<string, Set<OutcomeAllowance>> | undefined = undefined;
    private config: Config;
    private target: Country;

    private constructor(config: string) {
        if (Allowances.instance) {
            throw new SingletonError(this.constructor.name);
        }
        this.config = JSON.parse(config);
        this.target = getConfig().countrySpecific.country;
        this.allowedMarketCombinations = mapMarketPairsToMarketAllowances(this.config, this.target);
        this.allowedOutcomeCombinations = mapOutcomePairsToOutcomeAllowances(this.config, this.target);
    }

    public static getInstance(): Allowances {
        return instanceGuard(Allowances.instance);
    }

    public static createInstance(config: string) {
        if (!Allowances.instance) {
            Allowances.instance = new Allowances(config);
        }
        return Allowances.instance;
    }

    public validateMaxSelectionForEvent(selections: Selection[]): boolean {
        if (!this.config) {
            return false;
        }
        const maxSelections = Object.values(this.config).flatMap((sport) => sport.maxSelections ?? []);

        if (maxSelections.length === 0) {
            return true;
        }

        const outcomes = selections.map(Allowances.selectionToSelectedOutcome);
        return validateSelectionCountLimit(new Set(outcomes), maxSelections);
    }

    public validateSelection(
        selection1: Selection,
        selection2: Selection,
    ): boolean {
        if (!this.config || !this.allowedMarketCombinations || !this.allowedOutcomeCombinations) {
            return false;
        }

        const outcome1 = Allowances.selectionToSelectedOutcome(selection1);
        const outcome2 = Allowances.selectionToSelectedOutcome(selection2);

        if (!isMarketsCombinationValid(outcome1, outcome2, this.allowedMarketCombinations, this.target)) {
            return false;
        }

        return isOutcomesCombinationValid(outcome1, outcome2, this.target, this.allowedOutcomeCombinations);
    }

    static selectionToSelectedOutcome(selection: Selection): SelectedOutcome {
        const specifiers = safelyStringifySpecifiers(selection.odd!.specifiers);
        return {
            marketId: selection.marketId!,
            outcomeId: selection.odd!.id,
            specifiers: specifiers ? JSON.parse(specifiers) : undefined,
            isInPlay: selection.isOngoing,
        };
    }
}

function safelyStringifySpecifiers(specifiers?: Record<string, string>[] | null): string | undefined {
    try {
        return JSON.stringify(specifiers);
    } catch (e) {
        return '';
    }
}
